import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import DrawingFeature from "senf-atomic-design-system/src/components/atoms/map/DrawingFeature";
import MapViewEditor from "senf-atomic-design-system/src/components/atoms/map/MapViewEditor";
import SurveyGuide from "senf-atomic-design-system/src/components/special/survey/components/SurveyGuide";
// import { usePrepareSurveyPage } from "../legacy/hooks";
import { usePrepareSurveyPage } from "../hooks";

import type { Page, PageType } from "../survey.config";
import { isFeatureValid } from "../utils";

const SurveyMap = ({
  pages,
  index,
  project,
  formik,
  uid,
  mode,
  setOpenDescription,
  openDescription,
  statefulMap,
  setStatefulMap,
  featureId,
  setFeatureId,
  setFullscreenMap,
  fullscreenMap,
  isMobile,
  resize,
  setPages,
  handleSaveDrawn,
}) => {
  const { t } = useTranslation();
  const [drawMode, setDrawMode] = useState({ mode: null, drawType: null });
  const [descriptionProvided, setDescriptionProvided] = useState({});

  const { currentPage, currentPageMapData, currentPageIsMapbased } =
    usePrepareSurveyPage({
      pages,
      index,
      isMobile,
      project,
    });

  const mapPage =
    ((currentPage?.type === "mapPoint" ||
      currentPage?.type === "mapPath" ||
      currentPage?.type === "mapArea") &&
      (currentPage as Page<"mapPoint" | "mapPath" | "mapArea">)) ||
    undefined;

  useEffect(() => {
    if (formik.values?.[uid]?.geoData) {
      try {
        const geoDataObj = JSON.parse(formik.values[uid].geoData);
        const features = geoDataObj?.features;

        if (
          features &&
          (currentPage?.type === "mapPoint" ||
            currentPage?.type === "mapPath" ||
            currentPage?.type === "mapArea")
        ) {
          // Create an object mapping feature IDs to their description status
          const descriptionStatus = {};
          features.forEach((feature) => {
            descriptionStatus[feature.id] = isFeatureValid(
              feature,
              currentPage?.settings?.valueType,
            );
          });
          setDescriptionProvided(descriptionStatus);
        }
      } catch (error) {
        console.error("Error parsing geoData:", error);
      }
    }
  }, [
    formik.values?.[uid]?.geoData,
    currentPage?.type,
    (currentPage as Page<"mapPoint">)?.settings?.valueType,
    uid,
  ]);

  const resizeDependencies = useMemo(
    () => [isMobile, resize],
    [isMobile, resize],
  );
  const flyToDependencies = useMemo(() => {
    return [index, uid, currentPageMapData];
  }, [index, uid, currentPageMapData]);

  const fallbackRef = useRef(null);

  return (
    <MapViewEditor
      mapSettings={currentPage?.map?.settings}
      setMapSettings={(mapSettings) =>
        setPages((prev) => {
          return prev.map((e, idx) => {
            if (idx !== index) return e;
            if (mapSettings === null) {
              delete e.map.settings;
              return e;
            }
            return {
              ...e,
              map: {
                ...e.map,
                settings: {
                  ...e.map?.settings,
                  ...mapSettings,
                },
              },
            };
          });
        })
      }
      accessibilityInfotext={t(
        `map:accessibilityInfotext.drawingInfo.${currentPage?.type}`,
      )}
      allowFullscreen={isMobile && currentPageIsMapbased}
      mapData={currentPageMapData}
      resizeDependencies={resizeDependencies}
      flyToDependencies={flyToDependencies}
      shouldFlyTo={currentPage?.map?.settings}
      shouldTriggerRefresh={index}
      mode={mode}
      statefulMap={statefulMap}
      setStatefulMap={setStatefulMap}
      setFullscreenMap={setFullscreenMap}
      fullscreenMap={fullscreenMap}
      setShowMapDataSetList={() =>
        document.getElementById("mapBuilderOpenButton")?.click()
      }
    >
      <div
        ref={fallbackRef}
        className="absolute z-50 bottom-0 ps-5 pe-20 pb-7 flex gap-2 w-full pointer-events-none"
      >
        {ReactDOM.createPortal(
          <>
            {(!isMobile || !featureId) && (
              <SurveyGuide
                name={currentPage?.type as PageType}
                hide={!!featureId}
                onRefresh={!!currentPage?.type}
                classNames={{
                  content: "mx-5 mb-5",
                  container: "-order-1 pointer-events-auto me-auto",
                }}
              />
            )}

            {mode === "display" && (
              <DrawingFeature
                mapStyle={currentPageMapData?.mapStyle}
                mapType={drawMode}
                setDrawMode={setDrawMode}
                statefulMap={statefulMap}
                inModal={true}
                handleSaveDrawn={handleSaveDrawn}
                featureCollection={
                  formik?.values?.[uid]?.geoData &&
                  JSON.parse(formik?.values?.[uid]?.geoData)
                }
                featureId={featureId}
                setFeatureId={setFeatureId}
                setOpenDescriptionProp={
                  (mapPage?.settings?.valueType === "string" ||
                    mapPage?.settings?.valueType === "boolean") &&
                  setOpenDescription
                }
                maxFeatures={
                  mapPage?.settings?.mapResponse?.limit?.max || 10000
                }
                openDescription={openDescription}
                shouldTriggerRefresh={index}
                initialDrawType={
                  pages?.[index]?.type === "mapPoint"
                    ? "point"
                    : pages?.[index]?.type === "mapPath"
                      ? "path"
                      : "area"
                }
                descriptionProvided={descriptionProvided}
                isMobile={isMobile}
              />
            )}
          </>,
          (isMobile &&
            document.getElementById("survey-navigation-container")) ||
            fallbackRef.current ||
            document.body,
        )}
      </div>
    </MapViewEditor>
  );
};

export default SurveyMap;
